import React, { useContext, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { SessionContext } from '../../../context/SessionContext'
import { AuthContext } from '../../../context/AuthContext'
import { showToast } from '../../commonUnit/Toast'
import TaskCard from '../../topBar/TaskCard'
import TestList from '../../topBar/TestList'
import StarterCode from '../../topBar/StarterCode'
import {
  aiChatEnabled,
  aiChatSupport,
  describeSaveFailure,
  hidesAiColumn,
  saveSessionFields,
  testCodeForTestsSetting,
  testsEnabled,
} from '../sessionSettings'
import { updateTestCodeInSession } from '../../../service/testService'
import { getAiChatPolicy, getSessionById, saveAiChatPolicy } from '../../../service/sessionService'
import { supportsTestsToggle } from '../createWizard/sessionTypeCatalog'
import { formatTestsForRender } from '../../workspace/utils/TestCases/testDataFormatter'
import ReplayCodeEditor from '../../workspaceReplay2/components/ReplayCodeEditor'
import { studentSurfaceFor, workspaceShowsAiChat, workspaceShowsEndSurvey } from './studentSurface'
import VizmentalShell from './VizmentalShell'
import {
  READ_ONLY_RUN_TOOLTIP,
  VizmentalAiChat,
  VizmentalCodeEditor,
  VizmentalRunControls,
  VizmentalTaskSection,
  VizmentalTestSection,
  vizmentalTerminalText,
} from './vizmentalFacsimile'
import WorkspaceShell, {
  WorkspaceRunButton,
  WorkspaceTaskSection,
  WorkspaceTestSection,
  workspaceTerminalText,
} from './WorkspaceShell'
import { ChatReplica } from './WorkspacePreview'
import StudentPanelShell from './StudentPanelShell'
import { UngroupedChatNotice } from './StudentPanelPreview'
import {
  BlocklyFacsimile,
  ChatNoticeFacsimile,
  CodeBarFacsimile,
  CodeEditorFacsimile,
  CodeStuFrame,
  GroupRoomInputFacsimile,
  OutputFacsimile,
  PeerChatFacsimile,
  SqlResultFacsimile,
  SqlSchemaFacsimile,
  TaskCardFacsimile,
  TestTableFacsimile,
} from './studentPanelFacsimile'
import AiChatSettings from './AiChatSettings'
import '../../../css/studentview.scss'

// The "Session Setting" tab: the exercise laid out exactly as a student sees it
// (Canvas-style). Every part starts as a read-only preview of the student's screen; its
// Edit button swaps in an editor in the same place:
//   - task description: the existing TaskCard, opened straight into editing, Cancel / Save;
//   - student starter code: StarterCode's edit panel, Cancel / Save;
//   - test cases: a full-width dialog (TestList + the grading harness + the on/off switch),
//     since the student's test column is too narrow to configure them in;
//   - AI chat: the preview is the student's chat pane; Edit opens the AI Chat Policy.
// Cancel returns to the preview without saving; Save writes and returns to the preview,
// which then shows the change. AI chat itself is switched on and off with the ENABLE AI
// CHAT switch in the page header. Run is shown but disabled - instructors do not run code
// here.
//
// Nothing here mounts a real student component (Workspace, VizmentalIDE, StudentPanel,
// AIChat, CodeStu): those join the session, record telemetry and write messages on mount.
// The previews are context-free replicas of their markup.
//
// Which student page is mirrored follows studentSurfaceFor: 'workspace' (the Python family;
// GUI sessions never reach this tab - Home renders GuiExerciseEditor for them) gets the
// /workspace layout, 'vizmental' the classic VizmentalIDE, anything else StudentPanel.

// Persists a settings change against the live session, then publishes the row the server
// actually stored (so a backend that silently drops an unmigrated column shows as an
// error, not as a switch that flips back on the next poll).
const useSessionFieldSaver = () => {
  const { session, setSession } = useContext(SessionContext)
  const [saving, setSaving] = useState(false)
  const save = async (fields) => {
    if (!session?.id || !fields || !Object.keys(fields).length) return true
    setSaving(true)
    try {
      const result = await saveSessionFields(session.id, fields)
      if (result.session) setSession(result.session)
      if (!result.ok) {
        showToast(describeSaveFailure(result), 'error', 5000)
        return false
      }
      return true
    } finally {
      setSaving(false)
    }
  }

  // The test-case switch. Besides enable_tests, a C++ exercise's template has to follow
  // the toggle: its main() comes from the harness while tests are on and from the
  // template while they are off (sessionSettings.testCodeForTestsSetting - the same rule
  // the creation wizard applies).
  const setTestsOn = async (on) => {
    if (!(await save({ enable_tests: on }))) return
    if (session.type !== 'CPP') return
    // Built from a fresh read, not the render-time session, so a template save that was
    // still in flight is not written back over.
    const fresh = await getSessionById(session.id).catch(() => null)
    if (!fresh) return
    const nextCode = testCodeForTestsSetting(fresh, on)
    if (!nextCode) return
    if (!(await updateTestCodeInSession(session.id, nextCode))) {
      showToast('Test cases switched, but the C++ template could not be updated. Check its main().', 'warning', 6000)
      return
    }
    setSession((prev) => (prev && prev.id === session.id ? { ...prev, test_code: nextCode } : prev))
    showToast(
      on
        ? 'Test cases on: removed the empty main() from the template - the test harness provides main().'
        : 'Test cases off: added a main() to the C++ template so students can write and run a whole program.',
      'info',
      6000
    )
  }

  return { save, saving, setTestsOn }
}

// Pick up whatever the dialog panels wrote (TestList and the harness editors never call
// setSession) so the preview shows it as soon as a dialog closes.
const useRefreshSession = () => {
  const { session, setSession } = useContext(SessionContext)
  return async () => {
    if (!session?.id) return
    const fresh = await getSessionById(session.id).catch(() => null)
    if (fresh) setSession(fresh)
  }
}

const EditButton = ({ label, onClick }) => (
  <Button
    size="small"
    variant="outlined"
    startIcon={<EditOutlinedIcon fontSize="small" />}
    onClick={onClick}
    aria-label={label}
    sx={{ py: 0.1, lineHeight: 1.6, textTransform: 'none', flexShrink: 0, backgroundColor: '#fff' }}>
    Edit
  </Button>
)

// For parts whose student view has no header of its own (the editor, the chat pane, the
// task card): the Edit button floats over the preview's top-right corner.
const Editable = ({ canEdit, label, onEdit, children, className = '' }) => (
  <div className={`sv-editable ${className}`}>
    {canEdit && (
      <div className="sv-edit-overlay">
        <EditButton label={label} onClick={onEdit} />
      </div>
    )}
    {children}
  </div>
)

// Header row of an edit panel: title on the left, Cancel / Save on the right.
const EditPanelHead = ({ title, onCancel, onSave, saving = false, saveDisabled = false }) => (
  <div className="sv-panel-head">
    <span className="sv-panel-head__title">{title}</span>
    <Button size="small" onClick={onCancel} disabled={saving}>
      Cancel
    </Button>
    <Button size="small" variant="contained" onClick={onSave} disabled={saving || saveDisabled}>
      Save
    </Button>
  </div>
)

// Students see nothing at all when test cases are off; the instructor needs a handle to
// turn them back on, so this placeholder is the one instructor-only element in a preview.
const TestsOffPlaceholder = ({ canEdit, onEdit }) => (
  <div className="sv-muted-box sv-row">
    <span>Test cases are off — students don&apos;t see this section.</span>
    {canEdit && <EditButton label="Edit test cases" onClick={onEdit} />}
  </div>
)

// ---------------------------------------------------------------- dialogs

// codeEditing: the student starter code is open in its edit panel. For C++ the tests
// switch rewrites the template's main(), which that unsaved draft would silently undo on
// Save - so the switch waits until the draft is saved or cancelled.
const TestCasesDialog = ({ open, onClose, codeEditing = false }) => {
  const { session } = useContext(SessionContext)
  const { currentUser } = useContext(AuthContext)
  const { saving, setTestsOn } = useSessionFieldSaver()
  const hasTests = testsEnabled(session)
  const canEdit = currentUser?.role <= 1
  const canToggle = canEdit && supportsTestsToggle(session.type)
  const toggleBlocked = codeEditing && session.type === 'CPP'

  return (
    // keepMounted: TestList holds the list being edited in local state and never calls
    // setSession, so remounting it on every open would re-seed from a possibly stale copy.
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" keepMounted scroll="paper">
      <DialogTitle>Test cases</DialogTitle>
      <DialogContent dividers>
        {canToggle && (
          <div style={{ marginBottom: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={hasTests}
                  disabled={saving || toggleBlocked}
                  onChange={(e) => setTestsOn(e.target.checked)}
                />
              }
              label="Grade with test cases"
            />
            <div className="sv-caption" style={{ marginTop: 0 }}>
              {toggleBlocked
                ? 'Save or cancel the student code first - switching tests changes the C++ template\u2019s main().'
                : hasTests
                ? 'Students see a Test Cases list, and Run checks their code against it.'
                : 'Students see no Test Cases list, and Run just executes their code.'}
            </div>
          </div>
        )}
        {hasTests ? (
          <>
            <TestList forceView="editor" compact />
            <Accordion disableGutters sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">
                  Grading harness <span className="sv-caption">— hidden from students</span>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <StarterCode variant="harness" />
              </AccordionDetails>
            </Accordion>
          </>
        ) : (
          <div className="sv-muted-box">
            Test cases are off. Switch them on above to add cases for students.
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <span className="sv-caption" style={{ marginRight: 'auto', marginLeft: 12 }}>
          Changes in this window save as you make them.
        </span>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// SQL: the schema students query and the AI reference query they are graded against.
// StarterCode's SQL editor, unchanged.
const SqlSchemaDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
    <DialogTitle>Schema and reference query</DialogTitle>
    <DialogContent dividers>
      <StarterCode />
    </DialogContent>
    <DialogActions>
      <Button variant="contained" onClick={onClose}>
        Done
      </Button>
    </DialogActions>
  </Dialog>
)

// ---------------------------------------------------------------- AI chat pane

// Exactly what a student gets in the AI pane (see AIChat.jsx / VizmentalIDE.jsx).
const StudentAiPane = ({ session }) => {
  // A Python row whose chat was switched on elsewhere (grouping / Settings) still gets a
  // live AIChat, because AIChat only looks at enable_chat.
  if (aiChatEnabled(session) || (session.type === 'Python' && session.enable_chat)) {
    return <VizmentalAiChat />
  }
  if (session.closeSessionType === 'closed with survey') {
    return (
      <div style={{ padding: 16 }}>
        <div className="sv-muted-box">The session has ended — students see the end-of-session survey here.</div>
      </div>
    )
  }
  return (
    <Typography variant="h6" style={{ textAlign: 'center', marginTop: '20px' }}>
      AI Chat is currently disabled for this session.
    </Typography>
  )
}

// `preview`: the student's chat pane shown while not editing - by default VizmentalIDE's
// (StudentAiPane); the workspace layout passes its own chat replica.
const AiChatPane = ({ preview = null }) => {
  const { session } = useContext(SessionContext)
  const { currentUser } = useContext(AuthContext)
  const canEdit = currentUser?.role <= 1
  const [mode, setMode] = useState('preview')
  const [draft, setDraft] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // The policy is not part of the session object (it only leaves the server through the
  // instructor-only endpoint), so it is fetched when editing starts.
  const startEditing = async () => {
    setMode('edit')
    setLoading(true)
    const result = await getAiChatPolicy(session.id)
    setLoading(false)
    if (!result.ok) {
      showToast(
        result.status === 401 || result.status === 403
          ? 'Only a signed-in instructor can view the AI Chat Policy.'
          : 'Could not load the AI Chat Policy.',
        'error',
        5000
      )
      setMode('preview')
      return
    }
    setDraft(result.policy || '')
  }

  const handleSave = async () => {
    setSaving(true)
    const result = await saveAiChatPolicy(session.id, draft.trim() || null)
    setSaving(false)
    if (!result.ok) {
      showToast(
        result.status === 401 || result.status === 403
          ? 'Only instructors can change the AI Chat Policy. Try signing in again.'
          : 'Could not save the AI Chat Policy. Your text is still here - try again.',
        'error',
        5000
      )
      return
    }
    showToast('AI Chat Policy saved.', 'success', 2000)
    setMode('preview')
  }

  if (mode === 'edit') {
    return (
      <div className="sv-config-panel">
        <EditPanelHead
          title="AI Chat Policy"
          onCancel={() => setMode('preview')}
          onSave={handleSave}
          saving={saving}
          saveDisabled={loading}
        />
        <div className="sv-config-panel__settings">
          <AiChatSettings
            type={session.type}
            enabled
            policyOnly
            policy={draft}
            onPolicyChange={setDraft}
            disabled={loading || saving}
            context="tab"
            dense
          />
        </div>
      </div>
    )
  }

  return (
    <Editable canEdit={canEdit} label="Edit AI Chat Policy" onEdit={startEditing} className="sv-editable--fill">
      {preview || <StudentAiPane session={session} />}
    </Editable>
  )
}

// ---------------------------------------------------------------- Python / Vizmental

const VizmentalInstructorLayout = () => {
  const { session } = useContext(SessionContext)
  const { currentUser } = useContext(AuthContext)
  const refreshSession = useRefreshSession()
  const [taskMode, setTaskMode] = useState('preview')
  const [codeMode, setCodeMode] = useState('preview')
  const [testsOpen, setTestsOpen] = useState(false)

  const canEdit = currentUser?.role <= 1
  const hasTests = testsEnabled(session)
  const runLabel = hasTests ? 'Run Tests' : 'Run'
  const openTests = () => setTestsOpen(true)
  const closeTests = () => {
    setTestsOpen(false)
    refreshSession()
  }

  const task =
    canEdit && taskMode === 'edit' ? (
      <div className="content-section">
        <h3 className="section-title">Task Description</h3>
        <TaskCard hideQuiz autoEdit saveLabel="Save" onClose={() => setTaskMode('preview')} />
      </div>
    ) : (
      <VizmentalTaskSection
        task={session.task}
        actions={canEdit ? <EditButton label="Edit task description" onClick={() => setTaskMode('edit')} /> : null}
      />
    )

  const tests = hasTests ? (
    <VizmentalTestSection
      testList={session.test_list || []}
      actions={canEdit ? <EditButton label="Edit test cases" onClick={openTests} /> : null}
    />
  ) : (
    <div className="content-section">
      <h3 className="section-title">Test Cases</h3>
      <TestsOffPlaceholder canEdit={canEdit} onEdit={openTests} />
    </div>
  )

  const editor =
    canEdit && codeMode === 'edit' ? (
      <StarterCode variant="studentPane" onDone={() => setCodeMode('preview')} />
    ) : (
      <Editable
        canEdit={canEdit}
        label="Edit student starter code"
        onEdit={() => setCodeMode('edit')}
        className="sv-editable--fill">
        <VizmentalCodeEditor code={session.test_code?.template || ''} />
      </Editable>
    )

  return (
    <>
      <VizmentalShell
        task={task}
        tests={tests}
        editor={editor}
        runControls={<VizmentalRunControls label={runLabel} showTooltip />}
        terminal={vizmentalTerminalText(runLabel)}
        // With AI chat off students get no AI column at all - neither does the preview.
        chat={hidesAiColumn(session) ? null : <AiChatPane />}
      />
      {canEdit && (
        <TestCasesDialog open={testsOpen} onClose={closeTests} codeEditing={codeMode === 'edit'} />
      )}
    </>
  )
}

// ---------------------------------------------------------------- /workspace (Python family)

// What a Python / Vizmental student gets on /workspace/:id (Workspace.jsx, code session):
// task and tests on the left, editor and terminal in the middle, AI chat on the right only
// while it is on.
const WorkspaceInstructorLayout = () => {
  const { session } = useContext(SessionContext)
  const { currentUser } = useContext(AuthContext)
  const refreshSession = useRefreshSession()
  const [taskMode, setTaskMode] = useState('preview')
  const [codeMode, setCodeMode] = useState('preview')
  const [testsOpen, setTestsOpen] = useState(false)

  const canEdit = currentUser?.role <= 1
  const hasTests = testsEnabled(session)
  const runLabel = hasTests ? 'Run Tests' : 'Run'
  // Workspace.jsx's own rule: the per-session study override and the instructor's switch.
  const showChat = workspaceShowsAiChat(session)
  const openTests = () => setTestsOpen(true)
  const closeTests = () => {
    setTestsOpen(false)
    refreshSession()
  }

  const task =
    canEdit && taskMode === 'edit' ? (
      <div className="ws-content-section">
        <h3 className="ws-section-title">Task Description</h3>
        <TaskCard hideQuiz autoEdit saveLabel="Save" onClose={() => setTaskMode('preview')} />
      </div>
    ) : (
      <WorkspaceTaskSection
        task={session.task}
        actions={canEdit ? <EditButton label="Edit task description" onClick={() => setTaskMode('edit')} /> : null}
      />
    )

  // Never run here, so every row shows pending - what a student sees before their first Run.
  const tests = hasTests ? (
    <WorkspaceTestSection
      tests={formatTestsForRender(session, [])}
      actions={canEdit ? <EditButton label="Edit test cases" onClick={openTests} /> : null}
    />
  ) : (
    <div className="ws-content-section">
      <h3 className="ws-section-title">Test Cases</h3>
      <TestsOffPlaceholder canEdit={canEdit} onEdit={openTests} />
    </div>
  )

  const editor =
    canEdit && codeMode === 'edit' ? (
      <StarterCode variant="studentPane" onDone={() => setCodeMode('preview')} />
    ) : (
      <Editable
        canEdit={canEdit}
        label="Edit student starter code"
        onEdit={() => setCodeMode('edit')}
        className="sv-editable--fill svw-fill-column">
        <div className="svw-code-body">
          <ReplayCodeEditor code={session.test_code?.template || ''} language="python" />
        </div>
      </Editable>
    )

  return (
    <>
      <WorkspaceShell
        task={task}
        tests={tests}
        editor={editor}
        runControls={<WorkspaceRunButton label={runLabel} tooltip={READ_ONLY_RUN_TOOLTIP} />}
        terminal={workspaceTerminalText(runLabel)}
        chat={
          showChat ? (
            <AiChatPane preview={<ChatReplica withCamera={false} />} />
          ) : workspaceShowsEndSurvey(session) ? (
            // As /workspace: after End Session the column holds the review survey.
            <div style={{ padding: 16 }}>
              <div className="sv-muted-box">The session has ended — students see the end-of-session survey here.</div>
            </div>
          ) : null
        }
      />
      {canEdit && (
        <TestCasesDialog open={testsOpen} onClose={closeTests} codeEditing={codeMode === 'edit'} />
      )}
    </>
  )
}

// ---------------------------------------------------------------- CPP / SQL / Blockly / ...

const editorLanguageFor = (type) => {
  if (type === 'CPP') return 'cpp'
  if (type?.startsWith('SQL')) return 'sql'
  return 'python'
}

const StudentPanelInstructorLayout = () => {
  const { session } = useContext(SessionContext)
  const { currentUser } = useContext(AuthContext)
  const refreshSession = useRefreshSession()
  const [taskMode, setTaskMode] = useState('preview')
  const [codeMode, setCodeMode] = useState('preview')
  const [testsOpen, setTestsOpen] = useState(false)
  const [schemaOpen, setSchemaOpen] = useState(false)

  const type = session.type || ''
  const isSql = type.startsWith('SQL')
  const isBlockly = type.startsWith('Blockly')
  const isHelper = type === 'Helper/Helpee'
  const canEdit = currentUser?.role <= 1
  const hasTests = testsEnabled(session)
  const template = session.test_code?.template || ''
  const closeTests = () => {
    setTestsOpen(false)
    refreshSession()
  }
  const closeSchema = () => {
    setSchemaOpen(false)
    refreshSession()
  }

  const task =
    canEdit && taskMode === 'edit' ? (
      <TaskCard hideQuiz autoEdit saveLabel="Save" onClose={() => setTaskMode('preview')} />
    ) : (
      <Editable canEdit={canEdit} label="Edit task description" onEdit={() => setTaskMode('edit')}>
        <TaskCardFacsimile subject={session.subject} task={session.task} />
      </Editable>
    )

  let belowTask
  if (isSql) {
    belowTask = (
      <Editable canEdit={canEdit} label="Edit schema and reference query" onEdit={() => setSchemaOpen(true)}>
        <SqlSchemaFacsimile />
      </Editable>
    )
  } else if (hasTests) {
    belowTask = (
      <Editable canEdit={canEdit} label="Edit test cases" onEdit={() => setTestsOpen(true)}>
        <TestTableFacsimile testList={session.test_list || []} />
      </Editable>
    )
  } else {
    belowTask = <TestsOffPlaceholder canEdit={canEdit} onEdit={() => setTestsOpen(true)} />
  }

  // The code column as CodeStu draws it for a student: code bar, editor, output. Editing
  // the starter code swaps only the editor for StarterCode's Cancel / Save panel.
  let code
  if (isSql) {
    // Students write their query from scratch; the schema is configured on the left.
    code = (
      <CodeStuFrame after={<SqlResultFacsimile />}>
        <CodeBarFacsimile helpLabel="Ask for help" />
        <CodeEditorFacsimile code="" language="sql" />
      </CodeStuFrame>
    )
  } else if (canEdit && codeMode === 'edit') {
    code = (
      <div className="sv-code-column">
        <CodeStuFrame>
          <CodeBarFacsimile helpLabel={isHelper ? 'Ask for help' : null} />
        </CodeStuFrame>
        <div className="sv-code-column__editor">
          <StarterCode variant="studentPane" onDone={() => setCodeMode('preview')} />
        </div>
        <CodeStuFrame>
          <OutputFacsimile />
        </CodeStuFrame>
      </div>
    )
  } else {
    code = (
      <Editable canEdit={canEdit} label="Edit student starter code" onEdit={() => setCodeMode('edit')}>
        <CodeStuFrame>
          <CodeBarFacsimile helpLabel={isHelper ? 'Ask for help' : null} />
          {isBlockly ? (
            <BlocklyFacsimile code={template} />
          ) : (
            <CodeEditorFacsimile code={template} language={editorLanguageFor(type)} />
          )}
          <OutputFacsimile />
        </CodeStuFrame>
      </Editable>
    )
  }

  let chat = null
  if (isBlockly) chat = <GroupRoomInputFacsimile />
  else if (isHelper) {
    chat = (
      <ChatNoticeFacsimile>
        Use the &apos;Ask for Help&apos; or &apos;Offer Help to Others&apos; button to help and chat with
        other students
      </ChatNoticeFacsimile>
    )
  } else if (!isSql) {
    // What a newly joined student gets: Chat.jsx shows its "grouping session has concluded"
    // notice until they have a group, unless chat is on.
    chat = session.grouped || session.enable_chat ? <PeerChatFacsimile /> : <UngroupedChatNotice />
  }

  return (
    <>
      <StudentPanelShell
        left={
          <>
            {task}
            {belowTask}
          </>
        }
        code={code}
        chat={chat}
        height="100%"
      />
      {canEdit && !isSql && (
        <TestCasesDialog open={testsOpen} onClose={closeTests} codeEditing={codeMode === 'edit'} />
      )}
      {canEdit && isSql && <SqlSchemaDialog open={schemaOpen} onClose={closeSchema} />}
    </>
  )
}

// ---------------------------------------------------------------- entry

const StudentLayoutTaskPanel = () => {
  const { session } = useContext(SessionContext)
  if (!session) return null
  const surface = studentSurfaceFor(session)
  const hasAiChat = aiChatSupport(session.type) !== 'unavailable'
  return (
    <>
      <p className="sv-layout-toolbar__hint" style={{ margin: '4px 0 10px' }}>
        A preview of the student view. Use <strong>Edit</strong> on any part to change it;
        Run is disabled here.
        {hasAiChat && ' Turn AI chat on or off with ENABLE AI CHAT above.'}
      </p>
      <div className="sv-layout-frame">
        {surface === 'workspace' ? (
          <WorkspaceInstructorLayout />
        ) : surface === 'vizmental' ? (
          <VizmentalInstructorLayout />
        ) : (
          <StudentPanelInstructorLayout />
        )}
      </div>
    </>
  )
}

export default StudentLayoutTaskPanel
